import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from '../../toasts';
import moment from 'moment';
import { FaPlus } from 'react-icons/fa';
import FilterForm from '../../components/FilterForm';
import AssetSaleService from '../../services/assetSale.service';
import { getError, showCurrency } from '../../utils';
import { useLabels } from '../../hooks/useLabels';
import { usePermissions } from '../../hooks/usePermissions';
import Paginator from '../Paginator/Paginator';
import {
	EntityList,
	PageHead,
	StatStrip,
	ListToolbar,
	DataTable,
	Button,
	RowActionsMenu,
} from '../../components/EntityList';

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loading: true };
	case 'FETCH_SUCCESS':
		return {
			...state,
			assetSales: action.payload.assetSales,
			page: action.payload.page,
			pages: action.payload.pages,
			countAssetSales: action.payload.countAssetSales,
			pageSize: action.payload.pageSize,
			stats: action.payload.stats,
			loading: false,
		};
	case 'FETCH_FAIL':
		return { ...state, loading: false, error: action.payload };
	case 'DELETE_REQUEST':
		return { ...state, loadingDelete: true, successDelete: false };
	case 'DELETE_SUCCESS':
		return {
			...state,
			loadingDelete: false,
			successDelete: true,
		};
	case 'DELETE_FAIL':
		return { ...state, loadingDelete: false, successDelete: false };

	case 'DELETE_RESET':
		return { ...state, loadingDelete: false, successDelete: false };
	default:
		return state;
	}
};

function AssetSalesList() {
	const { t } = useLabels();
	const { canSee, canEdit, canCreate, canDelete } = usePermissions();
	const [{ loading, error, assetSales, successDelete, pageSize, countAssetSales, stats }, dispatch] =
		useReducer(reducer, {
			assetSales: [],
			loading: true,
			error: '',
			pageSize: 0,
			countAssetSales: 0,
			stats: [],
		});
	const initialLoading = loading && pageSize === 0;
	const navigate = useNavigate();
	const { search } = useLocation();
	const sp = new URLSearchParams(search);
	const column = sp.get('column') || 'customId';
	const direction = sp.get('direction') || 'asc';
	const currency = sp.get('currency') || 'all';
	const amountFrom = sp.get('amountFrom') || '';
	const amountTo = sp.get('amountTo') || '';
	const dateFrom = sp.get('dateFrom') || '';
	const dateTo = sp.get('dateTo') || '';
	const query = sp.get('query') || 'all';
	const page = sp.get('page') || 1;
	const [filtered, setFiltered] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const handleDropdownToggle = () => {
		setIsOpen(!isOpen);
	};

	const [filterOptions, setFilterOptions] = useState({
		formType: 'VentasActivo',
		currency: currency,
		dateFrom: dateFrom,
		dateTo: dateTo,
		amountFrom: amountFrom || '',
		amountTo: amountTo || '',
		query: query,
	});

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFilterOptions((prevOptions) => ({
			...prevOptions,
			[name]: value,
			formType: filterOptions.formType,
		}));
	};

	const handleApplyFilter = () => {
		const {
			currency,
			amountFrom,
			amountTo,
			dateFrom,
			dateTo,
		} = filterOptions;
		const filter = {
			currency,
			amountFrom,
			amountTo,
			dateFrom,
			dateTo,
			page: 1,
			query,
		};
		const filterUrl = getFilterUrl(filter);
		navigate(filterUrl);
		setFiltered(true);
	};

	useEffect(() => {
		handleApplyFilter();
	}, [filtered]);

	const handleClearFilter = () => {
		setFilterOptions({
			formType: 'VentasActivo',
			currency: 'all',
			amountFrom: '',
			amountTo: '',
			dateFrom: '',
			dateTo: '',
			query: 'all',
		});
		setFiltered(false);
	};

	const onPageChange = (page) => {
		navigate(getFilterUrl({ page: page }));
	};

	const handleSort = (sortColumn) => {
		const newSortDirection = sortColumn === column && direction === 'desc' ? 'asc' : 'desc';
		const newURL = `/AdminScreen/assetSales/search?page=${page}&query=${query}&column=${sortColumn}&direction=${newSortDirection}&currency=${currency}&amountFrom=${amountFrom}&amountTo=${amountTo}&dateFrom=${dateFrom}&dateTo=${dateTo}`;
		navigate(newURL);
	};

	useEffect(() => {
		const fetchData = async () => {
			try {
				dispatch({ type: 'FETCH_REQUEST' });
				const { data } = await AssetSaleService.search(
					`page=${page}&query=${query}&column=${column}&direction=${direction}&currency=${currency}&amountFrom=${amountFrom}&amountTo=${amountTo}&dateFrom=${dateFrom}&dateTo=${dateTo}`,
				);
				dispatch({ type: 'FETCH_SUCCESS', payload: data });
			} catch (err) {
				dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
				console.error(getError(err));
				toast.error(t('msg.listLoadFailed'));
			}
		};
		if (successDelete) {
			dispatch({ type: 'DELETE_RESET' });
		}
		fetchData();
	}, [
		successDelete,
		query,
		page,
		column,
		direction,
		currency,
		amountFrom,
		amountTo,
		dateFrom,
		dateTo,
	]);

	async function deleteAssetSaleHandler(assetSale) {
		if (
			window.confirm(
				t('msg.confirmDeleteAssetSale'),
			)
		) {
			try {
				dispatch({ type: 'DELETE_REQUEST' });
				await AssetSaleService.delete(assetSale._id);
				dispatch({ type: 'DELETE_SUCCESS' });
				toast.success(t('msg.assetSaleDeletedFull'));
			} catch (err) {
				dispatch({ type: 'DELETE_FAIL' });
				console.error(getError(err));
				toast.error(t('msg.assetSaleDeleteFailedFull'));
			}
		}
	}

	async function editAssetSaleHandler(assetSale) {
		navigate(`/AdminScreen/assetSales/editAssetSale/${assetSale._id}`, {
			state: { returnTo: '/AdminScreen/assetSales' },
		});
	}

	async function seeDetails(assetSale) {
		navigate(`/AdminScreen/assetSales/assetSale/${assetSale._id}`);
	}

	const getFilterUrl = (filter) => {
		const {
			query,
			currency,
			amountFrom,
			amountTo,
			dateFrom,
			dateTo,
		} = filterOptions;
		const filterQuery = filter.query || query;
		const filterPage = filter.page || 1;
		const filterCurrency = currency || 'all';
		const filterAmountFrom = amountFrom || '';
		const filterAmountTo = amountTo || '';
		const filterDateFrom = dateFrom || '';
		const filterDateTo = dateTo || '';

		const params = new URLSearchParams();
		params.append('page', filterPage);
		params.append('query', filterQuery);
		params.append('currency', filterCurrency);
		params.append('amountFrom', filterAmountFrom);
		params.append('amountTo', filterAmountTo);
		params.append('dateFrom', filterDateFrom);
		params.append('dateTo', filterDateTo);

		return `/AdminScreen/assetSales/search?${params.toString()}`;
	};

	const filterFormProps = {
		filterOptions: filterOptions,
		isOpen: isOpen,
		handleDropdownToggle: handleDropdownToggle,
		handleInputChange: handleInputChange,
		clearFilter: () => handleClearFilter(),
		handleApplyFilter: () => handleApplyFilter('VentasActivo'),
	};

	const filterBadgeCount = [
		currency !== 'all' && currency !== '',
		Boolean(amountFrom),
		Boolean(amountTo),
		Boolean(dateFrom),
		Boolean(dateTo),
	].filter(Boolean).length;

	const statStrips = useMemo(() => {
		const totals = (stats || []).reduce(
			(acc, s) => {
				acc.count += s.count;
				acc.withInitialPayment += s.withInitialPayment;
				acc.withPendingDebt += s.withPendingDebt;
				acc.settled += s.settled;
				return acc;
			},
			{ count: 0, withInitialPayment: 0, withPendingDebt: 0, settled: 0 },
		);

		// One tile per currency present, instead of stacking every currency's
		// amount inside a single tile — stacked lines stretch that tile's height
		// and throw off vertical centering across the rest of the row.
		const amountStats = (stats && stats.length > 0 ? stats : [null]).map((s) => ({
			label: s ? `Monto vendido ${showCurrency(s._id)}` : 'Monto total vendido',
			value: s ? s.totalAmount.toLocaleString('es-UY') : <span className='tw:text-ink-3'>—</span>,
			mono: true,
			accent: '--color-cat-3',
			sub: s ? `${s.count} ${s.count === 1 ? 'operación' : 'operaciones'}` : '0 operaciones',
		}));

		return [
			...amountStats,
			{
				label: 'Pago inicial cobrado',
				value: totals.withInitialPayment,
				sub: `${totals.withInitialPayment} con entrega inicial`,
			},
			{
				label: 'Saldo en deudas',
				value: totals.withPendingDebt,
				subTone: totals.withPendingDebt > 0 ? 'down' : 'neutral',
				sub: `${totals.withPendingDebt} con saldo pendiente`,
			},
			{
				label: 'Ventas saldadas',
				value: totals.settled,
				sub: 'totalmente cobradas',
			},
		];
	}, [stats]);

	const renderRowActions = (assetSale) => (
		<RowActionsMenu
			items={[
				canSee() && { label: 'Ver detalles', onClick: () => seeDetails(assetSale) },
				canEdit() && { label: 'Editar', onClick: () => editAssetSaleHandler(assetSale) },
				canDelete() && { label: 'Eliminar', onClick: () => deleteAssetSaleHandler(assetSale), danger: true },
			]}
		/>
	);

	const assetSaleTableColumns = [
		{
			key: 'operacion',
			header: 'Operación',
			className: 'tw:max-w-[420px]',
			render: (assetSale) => (
				<div>
					<a
						href={`/AdminScreen/assetSales/assetSale/${assetSale._id}`}
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							seeDetails(assetSale);
						}}
						className='tw:text-accent tw:font-medium tw:no-underline tw:hover:underline'
					>
						{assetSale.asset && assetSale.asset.name}
					</a>
					<div className='tw:text-[11.5px] tw:text-ink-3'>
						<span className='tw:font-mono'>{assetSale.customId || assetSale._id}</span> · Venta
					</div>
				</div>
			),
		},
		{
			key: 'date',
			header: 'Fecha',
			sortable: true,
			className: 'tw:font-mono tw:text-[12.5px] tw:text-ink-2',
			render: (assetSale) => (assetSale.date ? moment.utc(assetSale.date).format('DD-MM-YYYY') : <span className='tw:text-ink-3'>—</span>),
		},
		{
			key: 'lot',
			header: 'Padrón',
			className: 'tw:font-mono tw:text-[12px]',
			render: (assetSale) => (assetSale.lot ? <span className='tw:text-ink-1'>{assetSale.lot}</span> : <span className='tw:text-ink-4'>—</span>),
		},
		{
			key: 'purchaser',
			header: 'Comprador',
			className: 'tw:max-w-[180px] tw:truncate tw:text-ink-1',
			render: (assetSale) => (assetSale.purchaser && assetSale.purchaser.name) || <span className='tw:text-ink-3'>—</span>,
		},
		{
			key: 'amount',
			header: 'Monto',
			className: 'tw:text-right tw:font-mono tw:text-[13px] tw:font-medium',
			render: (assetSale) => `${showCurrency(assetSale.currency) || ''} ${(assetSale.amount || 0).toLocaleString('es-UY')}`,
		},
		{
			key: 'initialPayment',
			header: 'Pago inicial',
			className: 'tw:text-right tw:font-mono tw:text-[13px]',
			render: (assetSale) => (assetSale.initialPayment ? `${showCurrency(assetSale.currency) || ''} ${assetSale.initialPayment.toLocaleString('es-UY')}` : <span className='tw:text-ink-4'>—</span>),
		},
		{
			key: 'sumDebts',
			header: 'Total deudas',
			className: 'tw:text-right tw:font-mono tw:text-[13px]',
			render: (assetSale) => {
				if (assetSale.sumDebts === null || assetSale.sumDebts === undefined) {
					return <span className='tw:text-ink-4'>—</span>;
				}
				if (assetSale.sumDebts === 0) {
					return <span className='tw:text-ok'>Saldada</span>;
				}
				return <span className='tw:text-danger tw:font-medium'>{`${showCurrency(assetSale.currency) || ''} ${assetSale.sumDebts.toLocaleString('es-UY')}`}</span>;
			},
		},
	];

	return (
		<div>
			<Helmet>
				<title>{`Lista de ${t('entity.assetSale.plural')}`}</title>
			</Helmet>

			<FilterForm {...filterFormProps} />

			<EntityList>
				<PageHead
					breadcrumb={[{ label: 'Inicio', to: '/AdminScreen' }, { label: t('entity.assetSale.plural') }]}
					title={t('entity.assetSale.plural')}
					subtitle={`${initialLoading ? '—' : countAssetSales} ventas de activos · montos, pagos iniciales y saldos`}
					actions={
						canCreate() && (
							<Button
								variant='primary'
								icon={<FaPlus />}
								onClick={() => navigate('/AdminScreen/newAssetSale', { state: { returnTo: '/AdminScreen/assetSales' } })}
							>
								Agregar venta
							</Button>
						)
					}
				/>
				<div className='tw:mb-5'>
					<StatStrip stats={statStrips} loading={initialLoading} />
				</div>
				<ListToolbar
					query={query}
					getFilterUrl={getFilterUrl}
					navigate={navigate}
					searchPlaceholder='Comprador, activo, padrón, código…'
					onAdvancedFilter={handleDropdownToggle}
					filterBadgeCount={filterBadgeCount}
					onClearFilters={filterBadgeCount > 0 ? handleClearFilter : undefined}
					resultCount={initialLoading ? null : countAssetSales}
				/>
				<DataTable
					columns={assetSaleTableColumns}
					rows={assetSales}
					loading={loading}
					skeleton
					skeletonRows={pageSize || 15} // 15 matches routes/assetSaleRoutes.js PAGE_SIZE
					error={error}
					sortColumn={column}
					direction={direction}
					onSort={handleSort}
					onRowClick={(assetSale) => seeDetails(assetSale)}
					rowActions={canEdit() || canDelete() ? renderRowActions : undefined}
					emptyMessage='No se encontraron ventas con los filtros aplicados.'
				/>
				<Paginator
					className='tw:mt-4'
					totalCount={countAssetSales}
					pageSize={pageSize}
					currentPage={parseInt(page)}
					onPageChange={onPageChange}
				/>
			</EntityList>
		</div>
	);
}

export default AssetSalesList;
